/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-one-expression-per-line */
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Tags from '@base/Tags/Tags';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import { useSelector, useDispatch } from 'react-redux';
import H2 from '@base/H2/H2';
import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import Divider from '@base/Divider/Divider';
import textHelper from '@utils/textHelper';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import analyticsAction from '@actions/analytics';
import imageHelper from '@utils/imageHelper';
import { TextColors, Spacings, Fonts } from '@styles/index';
import useWindowSize from '@hooks/useWindowSize';
import TriangleUp from '../TriangleUp';
import MobileIconicSection from './MobileIconicSection';

const SecWrapper = styled.section`
  width: 100%;
  position: relative;
  margin-bottom: ${Spacings.SPACING_30B};
  @media (max-width: 70.95em) {
    margin-bottom: 56px;
  }
`;

const Container = styled.div`
  background-color: #fff;
  box-shadow: ${({ theme }) => boxShadowGenerator({ theme })};
  border-radius: 8px;
  top: -40px;
  position: relative;
  margin: 0 auto;
  text-align: center;
  width: 1136px;
  padding-top: 40px;
  @media (max-width: 70.95em) {
    width: 728px;
    padding-top: 32px;
  }
  @media (max-width: 47.95em) {
    top: 0;
  }
`;

const Heading = styled(H2)`
  margin: ${Spacings.SPACING_4B} auto;
  max-width: 420px;
  @media (max-width: 70.95em) {
    margin: 8px auto 4px;
  }
`;

const SubHeading = styled(H5)`
  font-weight: ${Fonts.REGULAR};
`;

const RowContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-left: 96px;
  margin-right: 96px;
  margin-top: 56px;
  @media (max-width: 70.95em) {
    margin: 40px 61px 16px 61px;
  }
`;

const LeftContentContainer = styled.div`
  text-align: right;
  width: 400px;
  padding: 30px 0;
  max-width: 320px;
  @media (max-width: 70.95em) {
    width: 238px;
    max-width: 238px;
    padding: 16px 0;
  }
`;

const MiddleContentContainer = styled.div`
  text-align: center;
  padding: 0 70px;
  position: relative;
  @media (max-width: 70.95em) {
    padding: 0 46px;
  }
`;

const RightContentContainer = styled(LeftContentContainer)`
  text-align: left;
`;

const Image = styled.img`
  user-drag: none;
  user-select: none;
  @media (max-width: 70.95em) {
    width: 56px;
    height: 56px;
  }
`;

const RoundIcon = styled(Image)`
  position: absolute;
  top: 33px;
  left: 59px;
  @media (max-width: 70.95em) {
    width: 24px;
    height: 24px;
    left: 34px;
    top: 28px;
  }
`;

const PlusIcon = styled(RoundIcon)`
  top: 8px;
  left: 52px;
  @media (max-width: 70.95em) {
    top: 8px;
    left: 28px;
    width: 36px;
    height: 40px;
  }
`;

const VerticalDivider = styled(Divider)`
  width: 2px;
`;

const LeftHeaderWrapper = styled(LeftContentContainer)`
  padding: 0;
`;

const RightHeaderWrapper = styled(RightContentContainer)`
  padding: 0;
`;

const RightHeaderContainer = styled.div`
  display: flex;
`;

const LeftHeaderContainer = styled(RightHeaderContainer)`
  justify-content: flex-end;
`;

const LeftHeader = styled(H4)`
  width: 207px;
  margin-right: 32px;
  @media (max-width: 70.95em) {
    font-size: 18px;
    line-height: 150%;
    margin-right: 24px;
    margin-bottom: 28px;
  }
`;

const RightHeader = styled(LeftHeader)`
  margin-left: 32px;
  @media (max-width: 70.95em) {
    margin-left: 24px;
  }
`;

const Description = styled.p`
  font-size: 16px;
  line-height: 150%;
  color: #808080;
  margin-top: ${Spacings.SPACING_2B};
  @media (max-width: 70.95em) {
    font-size: 14px;
  }
`;

const EndText = styled(Description)`
  font-size: ${Fonts.P1};
  margin: 14px 0 80px 0;
  @media (max-width: 70.95em) {
    margin: 0 auto 32px;
  }
`;

const ExamPricing = styled.span`
  color: ${TextColors.BLUE};
  padding-left: 5px;
  font-weight: bold;
  font-size: 24px;
  line-height: 120%;
`;

const ExamText = styled.span`
  font-weight: bold;
  font-size: 24px;
  line-height: 120%;
  color: #3c4852;
  @media (max-width: 70.95em) {
    font-size: 18px;
    display: inline-block;
  }
`;

const PrciningVal = styled.div`
  width: 600px;
  text-align: left;
  @media (max-width: 70.95em) {
    width: auto;
  }
`;

const Pricing = styled.div`
  padding: 64px 96px;
  display: flex;
  justify-content: space-between;
  @media (max-width: 70.95em) {
    padding: 27px 61px;
  }
`;

const LinkContainer = styled.div`
  align-self: center;
`;

const UNDivider = styled(Divider)`
  margin: 0 96px;
  @media (max-width: 70.95em) {
    margin: 0 61px;
    height: 2px;
  }
`;

const NewLine = styled.br`
  @media (max-width: 70.95em) {
    display: none;
  }
`;

const PrepLadderIconicSection = ({
  block,
  goalUID,
  goalSlug,
  countryCode,
  dataAnalytics
}) => {
  const { extraBlockinfo } = block;
  const { isMobile } = useWindowSize();
  const dispatch = useDispatch();

  let { iconicSubscriptionInfo } = useSelector(({ others }) => ({
    iconicSubscriptionInfo: others.iconicSubscriptionInfo?.[`iconic-${goalUID}`]
  }));

  if (goalUID === 'LEARNCP') {
    iconicSubscriptionInfo = {
      benefits: [
        {
          id: 1,
          benefitHeader: 'Learn from experts',
          benefitDesc:
            'Whether you are newbie or a seasoned developer, learn coding from the expert computer scientists',
          sortOrder: 1,
          benefitLogo: null
        },
        {
          id: 2,
          benefitHeader: 'Practice Problems',
          benefitDesc:
            'Get access to the best set of problems in the domain - solve and discuss what you have learned',
          sortOrder: 2,
          benefitLogo: null
        },
        {
          id: 3,
          benefitHeader: 'Structured courses',
          benefitDesc:
            'Our courses are structured to understand everything from theoretical knowledge to practical applications',
          sortOrder: 3,
          benefitLogo: null
        },
        {
          id: 4,
          benefitHeader: 'Online Contests',
          benefitDesc:
            'Prepare yourself for the multiple programming challenges that take place through-out the month on CodeChef',
          sortOrder: 4,
          benefitLogo: null
        }
      ]
    };
  }

  const goalCurrencyCode = textHelper.currencySymbolMap(countryCode);
  const handleAnalytics = () => {
    dispatch(
      analyticsAction(
        `${dataAnalytics}-viewPricing`,
        goalUID,
        {},
        { 'Last Primary Source': 'goal page' }
      )
    );
  };

  const ContentInfo =
    iconicSubscriptionInfo &&
    iconicSubscriptionInfo.benefits.map((infoData, index) => {
      const isEven = index % 2 === 0;
      const InfoContainer = isEven
        ? LeftContentContainer
        : RightContentContainer;
      return (
        <>
          <InfoContainer>
            <H4>{infoData.benefitHeader}</H4>
            <Description>{infoData.benefitDesc}</Description>
          </InfoContainer>
          {isEven && (
            <MiddleContentContainer>
              <VerticalDivider orientation="vertical" />
              <RoundIcon
                heigth="24"
                width="24"
                src={imageHelper.getImage(
                  goalUID === 'LEARNCP'
                    ? 'iconic_subs/codechefplusBullet.png'
                    : 'iconic_subs/neetpgIconicBullet.png'
                )}
                alt="-"
              />
            </MiddleContentContainer>
          )}
        </>
      );
    });

  const externalApps = useSelector(
    ({ topologies }) => topologies?.data?.[goalUID]?.externalApps
  );
  const externalAppName = externalApps[0] ? externalApps[0].display_name : '';
  const heading =
    goalUID === 'LEARNCP'
      ? 'Get a complete Hands-on learning experience'
      : 'ICONIC subscription';
  const subHeading = `Access to two of the best learning platforms, Unacademy & ${externalAppName}`;
  return isMobile ? (
    <MobileIconicSection
      type="Iconic"
      info={{
        heading,
        subHeading,
        logo: 'goal/un-prep.png',
        data: iconicSubscriptionInfo.benefits
      }}
    />
  ) : (
    <SecWrapper>
      <TriangleUp />
      <Container>
        {goalUID !== 'LEARNCP' && (
          <Tags type="filled" bgColor="#FFAD3B">
            Introducing
          </Tags>
        )}
        <Heading>{heading}</Heading>
        <SubHeading>{subHeading}</SubHeading>
        <RowContainer>
          <LeftHeaderWrapper>
            <LeftHeaderContainer>
              <LeftHeader>Get the best of Unacademy</LeftHeader>
              <Image
                width="56"
                height="56"
                src={imageHelper.getImage('iconic_subs/un.png')}
                alt="-"
              />
            </LeftHeaderContainer>
          </LeftHeaderWrapper>
          <MiddleContentContainer>
            <PlusIcon
              heigth="40.07px"
              width="36.18px"
              src={imageHelper.getImage(
                goalUID === 'LEARNCP'
                  ? 'iconic_subs/codechefplusBullet2.png'
                  : 'iconic_subs/neetpgIconicBullet2.png'
              )}
              alt="-"
            />
          </MiddleContentContainer>
          <RightHeaderWrapper>
            <RightHeaderContainer>
              <Image
                width="56"
                height="56"
                src={imageHelper.getImage(
                  goalUID === 'LEARNCP'
                    ? 'CodeChef.png'
                    : 'iconic_subs/prepladderIcon.png'
                )}
                alt="-"
              />
              <RightHeader>{`The best of ${externalAppName}`}</RightHeader>
            </RightHeaderContainer>
          </RightHeaderWrapper>
          {ContentInfo}
        </RowContainer>
        <EndText>and a lot more</EndText>
        <UNDivider />
        <Pricing>
          <PrciningVal>
            <ExamText>
              {`${extraBlockinfo?.goal_name} - ${
                goalUID === 'LEARNCP' ? '' : 'ICONIC'
              } subscription starts`}
              <br /> from <NewLine />
              <ExamPricing>
                {`${goalCurrencyCode}${textHelper.commas(
                  Math.round(extraBlockinfo?.lowest_price)
                )}`}
                /month
              </ExamPricing>
            </ExamText>
          </PrciningVal>
          <LinkContainer>
            <UnderlineLink
              color={TextColors.GREEN}
              href="/goal/[goalSlug]/[goalUID]/subscribe"
              as={`/goal/${goalSlug}/${goalUID}/subscribe`}
              onClick={handleAnalytics}
            >
              <H4
                color={TextColors.GREEN}
                data-analytics={`${dataAnalytics}-viewPricing`}
              >
                View pricing
              </H4>
            </UnderlineLink>
          </LinkContainer>
        </Pricing>
      </Container>
    </SecWrapper>
  );
};

PrepLadderIconicSection.propTypes = {
  block: PropTypes.objectOf(PropTypes.any).isRequired,
  goalUID: PropTypes.string.isRequired,
  goalSlug: PropTypes.string.isRequired,
  dataAnalytics: PropTypes.string
};

PrepLadderIconicSection.defaultProps = {
  dataAnalytics: ''
};

export default PrepLadderIconicSection;
